import { DeleteOutlined, MoreOutlined, SendOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client/react'
import { Card as AntdCard, Button, Checkbox, Dropdown, Tag, theme } from 'antd'
import { pick } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import {
  DeleteDocumentDocument,
  type DocumentFragment,
  DocumentStage,
  type ScanCenterCode,
  UnassignDocumentDocument,
  UpdateDocumentPaymentMetadataDocument,
  UpdateDocumentStageDocument,
} from '~app/graph/generated/graphql'
import { mainBlue, white } from '~app/helper/Colors'
import { emptyPayment } from '../../components/DocumentDialog'
import { clearMetadata } from '../../components/DocumentDialog/ClearPaymentMetadataButton'
import RuleWarnings from '../../components/DocumentDialog/RuleWarnings'

interface Props {
  id: string
  tags: string[]
  rules: DocumentFragment['rules']
  from: string
  subject?: string
  date: string
  scanId?: string
  scanCenter?: ScanCenterCode
  onClick?: () => any
  onDone?: () => any
  showActions?: boolean
  paymentMetadata: DocumentFragment['paymentMetadata'] | null
  isOpened: boolean
  stage: DocumentStage
}

type ActionType = 'send' | 'delete'

interface Action {
  key: ActionType
  label: string
  icon: React.ReactNode
  disabled: boolean
}

const StyledCard = styled(AntdCard)`
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  transition: box-shadow 200ms ease-in-out;
  cursor: pointer;
`

const DocumentTableRow = (props: Props) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [updateStage] = useMutation(UpdateDocumentStageDocument)
  const [updatePaymentMetadata] = useMutation(
    UpdateDocumentPaymentMetadataDocument
  )
  const [deleteOne] = useMutation(DeleteDocumentDocument)
  const [unassignOne] = useMutation(UnassignDocumentDocument)
  const [selected, setSelected] = useState(false)
  const color = mainBlue
  const { paymentMetadata } = props

  const payment =
    pick(paymentMetadata, ['amount', 'iban', 'recipient', 'reference']) ||
    emptyPayment
  const nonEmptyPaymentFields = Object.values(payment).filter(
    value => value !== null
  ).length
  const totalPaymentFields = Object.values(payment).length
  const noPaymentData = nonEmptyPaymentFields === 0

  const handleAction = async (action: ActionType) => {
    if (action === 'send') {
      await updateStage({
        variables: { id: props.id, stage: DocumentStage.Validated },
      })
      if (props.onDone) {
        props.onDone()
      }
    } else if (action === 'delete') {
      // unassigned documents will be deleted in the transfer process
      if (props.stage === DocumentStage.ManualProcessingStarted) {
        await unassignOne({ variables: { id: props.id } })
      } else {
        await deleteOne({ variables: { id: props.id } })
      }

      if (props.onDone) {
        props.onDone()
      }
    }
  }

  const actions: Action[] = [
    {
      key: 'send',
      label: t('misc.send'),
      icon: <SendOutlined />,
      disabled: false,
    },
    {
      key: 'delete',
      label: t('misc.delete'),
      icon: <DeleteOutlined />,
      disabled: false,
    },
  ]

  const menuProps = {
    onClick: ({ key }: { key: string }) => handleAction(key as ActionType),
    items: actions.map(action => ({
      key: action.key,
      label: action.label,
      icon: action.icon,
      disabled: action.disabled,
    })),
  }

  const deletePaymentData = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    clearMetadata(props.id, updatePaymentMetadata)
  }

  return (
    <StyledCard
      style={styles.tableRowStyle}
      styles={{ body: { padding: 0 } }}
      onClick={props.onClick}
    >
      <div
        role="button"
        style={{
          ...styles.contentContainerStyle,
          backgroundColor: selected ? color : token.colorBgContainer,
        }}
        onClick={e => {
          e.stopPropagation()
          props.onClick?.()
        }}
      >
        <div style={styles.selectContainerStyle}>
          <div
            style={{
              ...styles.indicatorStyle,
              backgroundColor: props.isOpened ? '#52c41a' : color,
            }}
          />
          <Checkbox
            checked={selected}
            onChange={e => {
              setSelected(e.target.checked)
            }}
          />
        </div>
        <div
          style={{
            ...styles.contentRowStyle,
            color: selected ? white : token.colorText,
          }}
        >
          <p style={{ ...styles.contentRowTextStyle, flex: 0.4 }}>
            {props.from ? props.from : t('documentView.unknown')}
          </p>
          <p
            style={{ ...styles.contentRowTextStyle, flex: 1, fontWeight: 500 }}
          >
            {props.subject ? props.subject : t('documentView.unknown')}
          </p>
          <div style={{ flex: 0.5 }}>
            {!noPaymentData && (
              <Tag
                color="red"
                closable
                onClose={deletePaymentData}
                onClick={deletePaymentData}
              >
                {t('documentView.clearPayment')}
              </Tag>
            )}
            <br />
            <Tag
              color={
                nonEmptyPaymentFields === 0
                  ? ''
                  : totalPaymentFields !== nonEmptyPaymentFields
                    ? 'red'
                    : 'green'
              }
            >
              {nonEmptyPaymentFields} / {totalPaymentFields}
            </Tag>
          </div>
          <p
            style={{
              ...styles.contentRowTextStyle,
              flex: 0.2,
              justifyItems: 'flex-start',
            }}
          >
            {props.date}
          </p>
        </div>
        {props.showActions && (
          <div onClick={e => e.stopPropagation()}>
            <Dropdown
              menu={menuProps}
              trigger={['click']}
              placement="bottomRight"
            >
              <Button
                type="text"
                icon={
                  <MoreOutlined
                    style={{
                      color: selected ? white : token.colorText,
                      fontSize: 20,
                    }}
                  />
                }
              />
            </Dropdown>
          </div>
        )}
      </div>
      <div
        style={{
          ...styles.tagRowStyle,
          backgroundColor: token.colorFillQuaternary,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <div style={{ display: 'flex' }}>
          {props.tags.map(tag => (
            <Tag key={tag} style={styles.tagStyle}>
              {tag.toUpperCase()}
            </Tag>
          ))}
        </div>
        {props.rules && <RuleWarnings rules={props.rules} />}

        <div style={styles.documentNumberStyle}>
          <Tag color="orange">{props.scanCenter}</Tag>
          {props.scanId && <Tag color="green">{props.scanId}</Tag>}
        </div>
      </div>
    </StyledCard>
  )
}

const styles: InlineStyles = {
  tableRowStyle: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginBottom: 20,
  },
  selectContainerStyle: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 60,
    height: '100%',
  },
  indicatorStyle: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: 10,
    height: '100%',
  },
  contentContainerStyle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: white,
    height: 70,
    width: '100%',
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
  },
  contentRowStyle: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  contentRowTextStyle: {
    margin: 0,
  },
  tagRowStyle: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: 10,
    paddingBottom: 10,
  },
  tagStyle: {
    marginLeft: 10,
  },
  documentNumberStyle: {
    paddingRight: 10,
  },
}

export default DocumentTableRow
